import { useEffect, useRef, useState } from 'react';
import { Box, SxProps } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { Header } from 'common/ui/Header';
import { AnalyseClearRequirementsButton } from 'modules/analyse/components/AnalyseClearRequirementsButton';
import { AnalysisRequirementInput } from 'modules/analyse/types/AnalysisRequirementInput';
import {
  AutomationTaskInput,
  CandidateBatchStatus
} from 'generated-graphql/types';
import { ATSSelectJobStep } from 'pages/CandidatesRanking/components/steps/ATSSelectJobStep';
import { AtsJobInput } from 'services/candidate/types';
import { ATSSelectRulesetStep } from 'pages/CandidatesRanking/components/steps/ATSSelectRulesetStep';
import { ATSCreateRulesetForMultipleJobsStep } from 'pages/CandidatesRanking/components/steps/ATSCreateRulesetForMultipleJobsStep';
import { GenerateRequirementsStep } from 'pages/CandidatesRanking/components/steps/GenerateRequirementsStep';
import { RequirementsSetupStep } from 'pages/CandidatesRanking/components/steps/RequirementsSetupStep';
import { RulesetFormHandle } from 'modules/analyse/components/RulesetForm';
import { ATSRequirementsSummaryStep } from 'pages/CandidatesRanking/components/steps/ATSRequirementsSummaryStep';
import { useOrganizationConfig } from 'core/stores/hooks/useOrganizationConfig';
import { ToastType, useToast } from 'services/toast';
import { GetCandidateBatchQuery } from 'generated-graphql/graphql';
import { ApolloGetItemType } from 'core/types/ApolloGetQuery';
import {
  layoutContainerPaddingBottom,
  layoutContainerPaddingTop,
  layoutContainerPaddingX
} from 'services/navigation/components/PrivateRoute';

const container: SxProps = {
  pt: layoutContainerPaddingTop,
  pb: layoutContainerPaddingBottom,
  px: layoutContainerPaddingX
};

const bodyContainer: SxProps = {
  mt: 4
};

enum ATSStepName {
  SELECT_JOB_STEP,
  SELECT_RULESET,
  CREATE_RULESET_FOR_MULTIPLE_JOBS,
  GENERATE_REQUIREMENTS,
  REQUIREMENTS_SETUP,
  REQUIREMENTS_SUMMARY
}

type Step = {
  name: ATSStepName;
  title: string;
  subtitle: string;
  component: React.ReactNode;
  handleBack: () => void;
};

type Props = {
  candidateBatch?: ApolloGetItemType<GetCandidateBatchQuery>;
};

export const ATSAnalyseCreation: React.FC<Props> = ({ candidateBatch }) => {
  const navigate = useNavigate();
  const organizationConfig = useOrganizationConfig();
  const { showToast } = useToast();

  const isNonDraftBatch =
    !!candidateBatch && candidateBatch.status !== CandidateBatchStatus.Draft;

  const initialStepName = candidateBatch?.id
    ? ATSStepName.SELECT_RULESET
    : ATSStepName.SELECT_JOB_STEP;

  const [stepName, setStepName] = useState(initialStepName);
  const [atsJobs, setAtsJobs] = useState<AtsJobInput[]>([]);
  const [hasGeneratedRequirements, setHasGeneratedRequirements] =
    useState(false);
  const [groupName, setGroupName] = useState<string>();
  const [requirements, setRequirements] = useState<AnalysisRequirementInput[]>(
    []
  );
  const [automationTasks, setAutomationTasks] = useState<AutomationTaskInput[]>(
    []
  );

  const [autofillDefaultTemplate, setAutofillDefaultTemplate] = useState(true);
  const [templateId, setTemplateId] = useState<string | undefined>();
  const rulesetFormRef = useRef<RulesetFormHandle>(null);

  useEffect(() => {
    if (!templateId && organizationConfig?.defaultAutomationTaskTemplateId) {
      setTemplateId(organizationConfig.defaultAutomationTaskTemplateId);
    }
  }, [organizationConfig]);

  useEffect(() => {
    if (isNonDraftBatch && candidateBatch) {
      showToast({
        title: 'Analysis already live',
        message: 'This job already has a live analysis — taking you to it.',
        type: ToastType.Warning
      });
      navigate(`${Paths.ANALYSE}/${candidateBatch.id}`, { replace: true });
    }
  }, [isNonDraftBatch, candidateBatch, navigate, showToast]);

  const handleBackGenerateRequirements = () => {
    if (candidateBatch?.id) {
      navigate(-1);
      return;
    }

    setStepName(ATSStepName.SELECT_JOB_STEP);
  };

  const noATSSteps: Step[] = [
    {
      name: ATSStepName.SELECT_JOB_STEP,
      title: 'Choose jobs',
      subtitle: 'Select all the jobs to use for this analysis',
      component: (
        <ATSSelectJobStep
          atsJobs={atsJobs}
          onNext={(atsJobs) => {
            setAtsJobs(atsJobs);
            setStepName(ATSStepName.SELECT_RULESET);
          }}
        />
      ),
      handleBack: () => {
        navigate(-1);
      }
    },
    {
      name: ATSStepName.SELECT_RULESET,
      title: 'Choose ruleset',
      subtitle: 'Choose a ruleset to analyse the selected role',
      component: (
        <ATSSelectRulesetStep
          atsJobs={atsJobs}
          candidateBatchId={candidateBatch?.id}
          onCreateRuleset={() => {
            setStepName(ATSStepName.CREATE_RULESET_FOR_MULTIPLE_JOBS);
          }}
          onSkip={() => {
            const step = hasGeneratedRequirements
              ? ATSStepName.REQUIREMENTS_SETUP
              : ATSStepName.GENERATE_REQUIREMENTS;
            setStepName(step);
          }}
        />
      ),
      handleBack: handleBackGenerateRequirements
    },
    {
      name: ATSStepName.CREATE_RULESET_FOR_MULTIPLE_JOBS,
      title: 'Analysis set up',
      subtitle: '',
      component: <ATSCreateRulesetForMultipleJobsStep atsJobs={atsJobs} />,
      handleBack: () => {
        setStepName(ATSStepName.SELECT_RULESET);
      }
    },
    {
      // NOTE: You can only get to flow if there is 1 job
      name: ATSStepName.GENERATE_REQUIREMENTS,
      title: 'Generate requirements',
      subtitle: '',
      component: (
        <GenerateRequirementsStep
          jobDescription={
            atsJobs[0]?.jobDescription ?? candidateBatch?.jobDescription
          }
          onGenerateRequirements={(requirements) => {
            setHasGeneratedRequirements(true);
            setRequirements(requirements);
            setStepName(ATSStepName.REQUIREMENTS_SETUP);
          }}
        />
      ),
      handleBack: () => {
        setStepName(ATSStepName.SELECT_RULESET);
      }
    },
    {
      name: ATSStepName.REQUIREMENTS_SETUP,
      title: 'Analysis set up',
      subtitle: '',
      component: (
        <RequirementsSetupStep
          formRef={rulesetFormRef}
          requirements={requirements}
          automationTasks={automationTasks}
          templateId={templateId}
          autofillDefaultTemplate={autofillDefaultTemplate}
          onNext={({ groupName, requirements, automationTasks }) => {
            setAutofillDefaultTemplate(false);
            setGroupName(groupName);
            setRequirements(requirements);
            setAutomationTasks(automationTasks);
            setStepName(ATSStepName.REQUIREMENTS_SUMMARY);
          }}
          onTemplateChange={(templateId) => {
            setTemplateId(templateId);
          }}
        />
      ),
      handleBack: () => {
        setStepName(ATSStepName.SELECT_RULESET);
      }
    },
    {
      name: ATSStepName.REQUIREMENTS_SUMMARY,
      title: 'Analysis summary',
      subtitle: '',
      component: (
        <ATSRequirementsSummaryStep
          atsJob={atsJobs[0]}
          candidateBatch={candidateBatch}
          candidateBatchGroupName={groupName}
          requirements={requirements}
          automationTasks={automationTasks}
          onBack={() => {
            setStepName(ATSStepName.REQUIREMENTS_SETUP);
          }}
        />
      ),
      handleBack: () => {
        setStepName(ATSStepName.REQUIREMENTS_SETUP);
      }
    }
  ];

  if (isNonDraftBatch) {
    return null;
  }

  const currentStep = noATSSteps.find((step) => step.name === stepName);

  if (!currentStep) {
    return <div>Step not found</div>;
  }

  return (
    <Box sx={container}>
      {currentStep.name !== ATSStepName.GENERATE_REQUIREMENTS && (
        <Header
          title={currentStep.title}
          items={[currentStep.subtitle]}
          breadcrumbs={[{ name: 'Analyse', path: Paths.ANALYSE }]}
          currentBreadcrumb={currentStep.title}
          onBack={currentStep.handleBack}
          actions={
            currentStep.name === ATSStepName.REQUIREMENTS_SETUP ? (
              <AnalyseClearRequirementsButton
                onClearRequirements={() =>
                  rulesetFormRef.current?.clearRequirements()
                }
              />
            ) : undefined
          }
        />
      )}
      <Box sx={bodyContainer}>{currentStep.component}</Box>
    </Box>
  );
};
